import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import OAuth from '../components/OAuth';
import { toast } from 'react-toastify';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import ForgotPasswordSvg from '../assets/svg/Forgot password.svg';

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  function onChange(e) {
    setEmail(e.target.value);
  }

  function handleFocus() {
    setIsFocused(true);
  }

  function handleBlur() {
    if (!email) {
      setIsFocused(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    try {
      const auth = getAuth()
      await sendPasswordResetEmail(auth, email);
      toast.success("Check your email or spam folder for the reset password link");
    } catch (error) {
      toast.error("Could not send reset password email");
    }
  }

  return (
    <section className="bg-black min-h-screen relative overflow-hidden">
      {/* Floating movie elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-3 pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div 
            key={i}
            className="absolute bg-white rounded-full"
            style={{
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 12 + 8}s linear infinite`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: Math.random() * 0.3 + 0.1
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 pt-4 pb-8">
        <h1 className="text-3xl md:text-4xl text-center font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#4da3ff] via-[#FF1493] to-pink-400 animate-gradient-x">
          Reset Your Password
        </h1>
        <p className="text-center text-gray-400 mt-1 text-sm md:text-base">
          Enter your email to receive a reset link.
        </p>

        <div className="flex flex-col lg:flex-row justify-center items-center gap-6 mt-6">
          {/* Illustration Container */}
          <div className="w-full lg:w-1/2 mb-6 lg:mb-0 flex justify-center items-center">
            <img
              src={ForgotPasswordSvg}
              alt="Forgot password illustration"
              className="w-full max-w-md h-auto"
            />
          </div>

          {/* Form Container */}
          <div className="w-full lg:w-1/2 lg:max-w-md">
            <form onSubmit={onSubmit} className="bg-gray-900 bg-opacity-70 backdrop-blur-sm p-5 rounded-xl border border-gray-800">
              <div className="relative mb-6">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={onChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  className="w-full px-4 py-3 text-white bg-gray-800 border border-gray-700 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-[#FF1493]"
                />
                <label 
                  htmlFor="email"
                  className={`absolute left-4 transition-all duration-200 ease-in-out pointer-events-none ${isFocused || email ? 'top-1 text-xs text-gray-400' : 'top-3.5 text-gray-500'}`}
                >
                  Email address
                </label>
              </div>

              <button
                className="w-full text-white px-5 py-3 text-sm font-bold uppercase rounded-full shadow-lg transition duration-150 ease-in-out hover:shadow-xl bg-gradient-to-r from-[#4da3ff] via-[#FF1493] to-pink-400 animate-gradient-x"
                type="submit"
              >
                Send Reset Link
              </button>

              <div className="flex justify-between whitespace-nowrap text-sm mt-5">
                <p className="text-gray-400">
                  Don't have an account?
                  <Link
                    to="/sign-up"
                    className="text-[#4da3ff] hover:text-[#FF1493] transition duration-200 ease-out ml-1"
                  >
                    Register
                  </Link>
                </p>
                <p className="text-gray-400 hover:text-[#FF1493] transition duration-200 ease-out">
                  <Link to="/sign-in">Sign In instead</Link>
                </p>
              </div>

              <div className="flex items-center my-4 before:border-t before:flex-1 before:border-gray-700 after:border-t after:flex-1 after:border-gray-700">
                <p className="text-center font-semibold mx-4 text-gray-400 text-sm">OR</p>
              </div>
              
              <OAuth />
            </form>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); opacity: 0.1; }
          50% { opacity: 0.2; }
          100% { transform: translateY(-100vh) rotate(180deg); opacity: 0; }
        }
        @keyframes gradient-x {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </section>
  );
}